import { type ReactNode } from 'react';
import { Icon } from '@/components/ui/Icon';
import { useSession } from '@/store/session';

/** Coins, Kyat, level. Kyat is a string all the way from the server — never a float. */
export function Hud() {
  const user = useSession((s) => s.user);
  const wallet = useSession((s) => s.wallet);
  const energy = useSession((s) => s.energy);

  const xpPct = user && user.xp_to_next > 0 ? (user.xp / user.xp_to_next) * 100 : 0;

  return (
    <header className="relative z-20 border-b border-[var(--hairline)]
                       bg-[linear-gradient(var(--ui-panel)_60%,rgba(32,20,16,0))]
                       px-2.5 pb-2 pt-[calc(env(safe-area-inset-top)+8px)]">
      <div className="flex items-center gap-1.5">
        <Chip tone="coin" icon={<Icon name="coin" size={14} className="text-coin" />} value={wallet.coins.toLocaleString('en-US')} label="Shwe Coins" />
        <Chip tone="kyat" icon={<Icon name="kyat" size={14} className="text-kyat-glow" />} value={wallet.kyat} label="Shwe Kyat" />
        <div className="flex-1" />
        <Chip tone="plain" icon={<Icon name="energy" size={14} className="text-amber-400" />} value={`${energy.current}/${energy.max}`} label="Energy" />
      </div>

      {user && (
        <div className="mt-1.5 flex items-center gap-2">
          <span className="font-pixel text-[6.5px] text-ink-dim">LV.{user.level}</span>
          <span className="relative h-1 flex-1 overflow-hidden rounded-full bg-white/10">
            <i
              className="absolute inset-y-0 left-0 block rounded-full
                         bg-[linear-gradient(90deg,#5AB7FF,#B77BFF)] transition-[width] duration-300"
              style={{ width: `${xpPct}%` }}
            />
          </span>
          <span className="text-[10px] font-bold text-ink-dim tabular">
            {user.xp}/{user.xp_to_next}
          </span>
        </div>
      )}
    </header>
  );
}

function Chip({ tone, icon, value, label }: {
  tone: 'coin' | 'kyat' | 'plain'; icon: ReactNode; value: string; label: string;
}) {
  const colour =
    tone === 'coin' ? 'text-coin border-[rgba(245,197,66,.26)]'
    : tone === 'kyat' ? 'text-kyat border-[rgba(169,107,240,.26)]'
    : 'text-ink border-[var(--hairline)]';
  return (
    <span className={`flex items-center gap-1.5 rounded-sm border bg-[var(--surface)]
                      px-2 py-1.5 text-[11.5px] font-bold tabular backdrop-blur ${colour}`}>
      <span aria-hidden>{icon}</span>
      <span>{value}</span>
      <span className="sr-only">{label}</span>
    </span>
  );
}
